import clsx from "clsx";
import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import tradeDataStore from "@/store/tradeDataStore";
import {
  isFillLocked,
  isFillCancelled,
  isFillFixEditLocked,
  getFillFromStore,
  unlockFill,
  FIX_LOCKED_FILL_REASON,
} from "../fillStateHelpers";
import { pencil } from "@/components/icons/SVGIcons";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";

// Hide at Filled/NoMoreFill/Affirmed/Re-Sent (trade is complete / allowing the OP to finish / awaiting re-sent acknowledgement - RP can't edit)
// Show at ChangePending (RP is editing again) and Working
export const statusToRemoveFillButtons = [
  ExecutionStrategyStatus.Filled,
  ExecutionStrategyStatus.NoMoreFills,
  ExecutionStrategyStatus.Affirmed,
  ExecutionStrategyStatus.Recalled,
  ExecutionStrategyStatus.ReSent,
  ExecutionStrategyStatus.Acknowledged,
  ExecutionStrategyStatus.Rejected,
  ExecutionStrategyStatus.NotDealt,
  ExecutionStrategyStatus.Expired, // LQC-3249 - OP must extend expiry first
];

/**
 * EditFillButtonCellRenderer
 *
 * Renders an "Edit" (pencil) button in the Order Fills table.
 * Only shown for locked fills (fills with a timestamp in filledAt).
 * When clicked, unlocks the fill by changing filledAt to "Updated (Pending)",
 * allowing the user to edit the fill's price and quantity.
 *
 * Not shown for:
 * - Cancelled fills
 * - New fills
 * - Already unlocked fills (Updated Pending)
 * - OP trades (only RP can edit)
 *
 * Disabled with a tooltip on an Ardea or LDC FIX order (LQC-3241); the RP removes and re-adds instead.
 */
const EditFillButtonCellRenderer = (params: any) => {
  // Subscribe to tradeData for reactive updates (hooks must run before any early return)
  const tradeData = tradeDataStore((state) => state.tradeData);

  if (!params.data) {
    return null;
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId;
  const isRP = fullTradeData?.role === "RP";
  const fillId = params.data.fillId;
  const extFillId = params.data.fillData?.extFillId;

  // Only show button for RP trades
  if (!isRP) {
    return null;
  }

  const currentTrade = tradeData.find((trade) => trade.esid === esid);
  const status = currentTrade?.status ?? fullTradeData?.status;

  // Hide at Filled/NoMoreFill/Affirmed (trade is complete / allowing the OP to finish - RP can't edit)
  // Show at ChangePending (RP is editing again) and Working
  if (
    status &&
    statusToRemoveFillButtons.includes(status as ExecutionStrategyStatus)
  ) {
    return null;
  }

  // Get the fill from the store to check its state
  // extFillId fallback: when fillId is stale after backend assigns new fillId
  const fill =
    esid && fillId !== undefined && blockId !== undefined
      ? getFillFromStore(tradeData, esid, blockId, fillId, extFillId)
      : null;

  // Only show for locked fills (not cancelled, not new, not already unlocked)
  const isLocked = fill ? isFillLocked(fill) : false;
  const isCancelled = fill ? isFillCancelled(fill) : false;

  if (!isLocked || isCancelled) {
    return null;
  }

  const isFixLocked = isFillFixEditLocked(currentTrade ?? fullTradeData, blockId);

  const handleEditFill = () => {
    if (esid && blockId !== undefined && fillId !== undefined) {
      // Unlock the fill (extFillId fallback for stale fillId)
      const resolvedFillId = unlockFill(esid, blockId, fillId, extFillId);

      // Update stale fillId on params.data so subsequent interactions use the correct fillId
      if (resolvedFillId !== undefined && resolvedFillId !== fillId) {
        params.data.fillId = resolvedFillId;
      }

      // Refresh the grid to update the cells
      if (params.api) {
        params.api.refreshCells({ force: true, suppressFlash: true });
      }
    }
  };

  return (
    <div
      data-testid="editFillButtonCellContainer"
      className={clsx(
        "flex h-full items-center justify-center",
        isFixLocked && "cursor-not-allowed",
      )}
      // Chrome drops hovers on a disabled button, so the cell carries the tooltip.
      title={isFixLocked ? FIX_LOCKED_FILL_REASON : undefined}
    >
      <button
        onClick={handleEditFill}
        disabled={isFixLocked}
        // className="h-6 w-6 rounded bg-blue-500 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
        className={clsx(
          "flex min-h-[20px] min-w-[20px] items-center justify-center rounded bg-gray-400/50 text-xs font-semibold text-black focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-500/50 dark:text-white",
          isFixLocked
            ? "pointer-events-none opacity-40"
            : "opacity-90 hover:bg-blue-500/90 dark:hover:bg-blue-600/80",
        )}
        title={isFixLocked ? undefined : "Edit fill"}
      >
        {pencil}
      </button>
    </div>
  );
};

export default EditFillButtonCellRenderer;
